import React from 'react';
import styles from './assetsformpage.module.css';

export default function ChartPreview({ preset = 'bar', labels = [], showAxis = true }) {
  const n = Math.max(1, labels.length || 1);
  const values = Array(n).fill(1);

  return (
    <div className={styles.chartPreview}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className={styles.chartSvg}>
        {preset === 'bar' && <BarChart values={values} labels={labels} showAxis={showAxis} />}
        {preset === 'column' && <ColumnChart values={values} labels={labels} showAxis={showAxis} />}
        {preset === 'pie' && <PieChart values={values} labels={labels} donut={false} />}
        {preset === 'donut' && <PieChart values={values} labels={labels} donut />}
        {preset === 'radar' && <RadarChart values={values} labels={labels} />}
      </svg>
    </div>
  );
}

const PAD = { top: 10, right: 12, bottom: 12, left: 10 };

function BarChart({ values, labels, showAxis }) {
  const maxLen = (labels || []).reduce((m, s) => Math.max(m, (s || '').length), 0);
  const approxPerChar = 2.0;
  const leftPadDynamic = Math.min(28, 8 + maxLen * approxPerChar);
  const left = PAD.left + leftPadDynamic;

  const innerW = 100 - left - PAD.right;
  const innerH = 100 - PAD.top - PAD.bottom;
  const baseY = PAD.top;

  const n = values.length;
  const rowH = innerH / n;
  const maxVal = Math.max(...values);
  const barGap = Math.min(2, rowH * 0.15);

  return (
    <>
      {showAxis && (
        <>
          <line x1={left} y1={baseY} x2={left} y2={baseY + innerH} stroke="#94a3b8" strokeWidth="0.5" />
          <line x1={left} y1={baseY + innerH} x2={left + innerW} y2={baseY + innerH} stroke="#94a3b8" strokeWidth="0.5" />
        </>
      )}
      {values.map((v, i) => {
        const w = (v / maxVal) * Math.max(0, innerW - 6);
        const y = baseY + i * rowH + barGap;
        const h = rowH - barGap * 2;
        return (
          <g key={i}>
            <text x={left - 2} y={y + h / 2 + 1.6} fontSize="3.6" fill="#334155" textAnchor="end">
              {labels[i] || `L${i+1}`}
            </text>
            <rect x={left + 2} y={y} width={w} height={h} fill="#CBD5E1" stroke="#94A3B8" strokeWidth="0.3" />
          </g>
        );
      })}
    </>
  );
}

function ColumnChart({ values, labels, showAxis }) {
  const innerW = 100 - PAD.left - PAD.right;
  const innerH = 100 - PAD.top - PAD.bottom;
  const baseX = PAD.left;
  const baseY = PAD.top;

  const n = values.length;
  const colW = innerW / n;
  const maxVal = Math.max(...values);
  const gap = Math.min(2, colW * 0.15);

  return (
    <>
      {showAxis && (
        <>
          <line x1={baseX} y1={baseY} x2={baseX} y2={baseY + innerH} stroke="#94a3b8" strokeWidth="0.5" />
          <line x1={baseX} y1={baseY + innerH} x2={baseX + innerW} y2={baseY + innerH} stroke="#94a3b8" strokeWidth="0.5" />
        </>
      )}
      {values.map((v, i) => {
        const h = (v / maxVal) * (innerH - 6);
        const x = baseX + i * colW + gap;
        const y = baseY + innerH - h;
        const w = Math.max(1, colW - gap * 2);
        return (
          <g key={i}>
            <rect x={x} y={y} width={w} height={h} fill="#CBD5E1" stroke="#94A3B8" strokeWidth="0.3" />
            <text x={x + w / 2} y={baseY + innerH + 5.5} fontSize="3.6" fill="#334155" textAnchor="middle">
              {labels[i] || `L${i+1}`}
            </text>
          </g>
        );
      })}
    </>
  );
}

function PieChart({ values, labels, donut }) {
  const cx = 50, cy = 50;
  const rOuter = Math.min((100 - PAD.left - PAD.right), (100 - PAD.top - PAD.bottom)) / 2 - 2;
  const r = Math.max(12, rOuter);
  const rInner = donut ? r * 0.55 : 0;
  const total = values.reduce((a, b) => a + b, 0) || 1;

  let startAngle = -Math.PI / 2;
  return (
    <>
      {values.map((v, i) => {
        const angle = (v / total) * Math.PI * 2;
        const endAngle = startAngle + angle;

        const path = describeArc(cx, cy, r, rInner, startAngle, endAngle);

        const mid = (startAngle + endAngle) / 2;
        const lx = cx + (r + 6) * Math.cos(mid);
        const ly = cy + (r + 6) * Math.sin(mid);

        let anchor = 'middle';
        if (Math.cos(mid) > 0.3) anchor = 'start';
        else if (Math.cos(mid) < -0.3) anchor = 'end';

        startAngle = endAngle;
        return (
          <g key={i}>
            <path d={path} fill="#CBD5E1" stroke="#94A3B8" strokeWidth="0.3" />
            {labels[i] && (
              <text x={lx} y={ly} fontSize="3.6" fill="#334155" textAnchor={anchor}>
                {labels[i]}
              </text>
            )}
          </g>
        );
      })}
    </>
  );
}

function describeArc(cx, cy, rOuter, rInner, start, end) {
  const x0 = cx + rOuter * Math.cos(start);
  const y0 = cy + rOuter * Math.sin(start);
  const x1 = cx + rOuter * Math.cos(end);
  const y1 = cy + rOuter * Math.sin(end);
  const large = end - start > Math.PI ? 1 : 0;

  if (rInner <= 0) {
    return [`M ${cx} ${cy}`, `L ${x0} ${y0}`, `A ${rOuter} ${rOuter} 0 ${large} 1 ${x1} ${y1}`, 'Z'].join(' ');
  }

  const xi0 = cx + rInner * Math.cos(end);
  const yi0 = cy + rInner * Math.sin(end);
  const xi1 = cx + rInner * Math.cos(start);
  const yi1 = cy + rInner * Math.sin(start);

  return [
    `M ${x0} ${y0}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${x1} ${y1}`,
    `L ${xi0} ${yi0}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${xi1} ${yi1}`,
    'Z'
  ].join(' ');
}

function RadarChart({ values, labels }) {
  const cx = 50, cy = 50;
  const r = Math.min((100 - PAD.left - PAD.right), (100 - PAD.top - PAD.bottom)) / 2 - 2;
  const n = values.length;
  if (n === 0) return null;

  const gridR = [r * 0.33, r * 0.66, r];
  return (
    <>
      {gridR.map((gr, idx) => (
        <circle key={idx} cx={cx} cy={cy} r={gr} fill="none" stroke="#E2E8F0" strokeWidth="0.4" />
      ))}
      {labels.map((_, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        const x = cx + r * Math.cos(a);
        const y = cy + r * Math.sin(a);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#E2E8F0" strokeWidth="0.4" />;
      })}

      <polygon
        points={labels.map((_, i) => {
          const a = (i / n) * Math.PI * 2 - Math.PI / 2;
          const rr = r * 0.7;
          const x = cx + rr * Math.cos(a);
          const y = cy + rr * Math.sin(a);
          return `${x},${y}`;
        }).join(' ')}
        fill="rgba(203,213,225,0.6)"
        stroke="#94A3B8"
        strokeWidth="0.6"
      />

      {labels.map((lb, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        const lx = cx + (r + 5) * Math.cos(a);
        const ly = cy + (r + 5) * Math.sin(a);
        return (
          <text key={i} x={lx} y={ly} fontSize="3.6" textAnchor="middle" fill="#334155">
            {lb || `L${i+1}`}
          </text>
        );
      })}
    </>
  );
}